// ─────────────────────────────────────────────────────────────────
//  PatientDashboard.jsx  —  v3 with dependent switching + default
//  FIX: the auto-switch useEffect now lives BELOW the
//  handleSwitchDependent declaration. Its dependency array is read
//  during render, and a `const` (even a useCallback one) cannot be
//  referenced above its own line — that was the
//  "Cannot access 'handleSwitchDependent' before initialization".
// ─────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Flex, Spin, Alert } from "antd";
import { UserSwitchOutlined } from "@ant-design/icons";

import { injectPatientStyles, SURF, RADIUS_SM, FF_BODY } from "../../components/patientTokens";
import { DesktopSidebar, MobileDrawer, BottomTabBar } from "../../components/PatientSidebar";
import PatientTopBar          from "../../components/PatientTopBar";
import PatientOverview        from "../../components/PatientOverview";
import PatientVisitHistory    from "../../components/PatientVisitHistory";
import PatientInvestigation   from "../../components/PatientInvestigation";
import PatientProfile         from "../../components/PatientProfile";
import PatientAppointments    from "../../components/PatientAppointments";
import {
  useDependents,
  DependentAvatarRail,
  DependentDrawer,
  DependentSwitcherBtn,
} from "../../components/PatientDependents";

function PatientDashboard() {
  const navigate = useNavigate();
  const didAutoSwitch = useRef(false);

  // ── Auth ────────────────────────────────────────────────────
  const token   = localStorage.getItem("patientToken");
  const decoded = token ? jwtDecode(token) : {};

  // ── Layout state ────────────────────────────────────────────
  const [activeNav,        setActiveNav]        = useState("overview");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen,   setMobileMenuOpen]   = useState(false);
  const [depDrawerOpen,    setDepDrawerOpen]    = useState(false);

  // ── Active patient (primary by default) ─────────────────────
  const [activePrn,     setActivePrn]     = useState(decoded?.prn || "");
  const [activePatient, setActivePatient] = useState(null);

  // ── Shared data ─────────────────────────────────────────────
  const [primaryPatient, setPrimaryPatient] = useState(null);
  const [visits,         setVisits]         = useState([]);
  const [appointments,   setAppointments]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [switchLoading,  setSwitchLoading]  = useState(false);
  const [error,          setError]          = useState(null);

  // ── Dependents ──────────────────────────────────────────────
  const { dependents, loading: depsLoading, refetch: refetchDeps } = useDependents(token);


  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => { injectPatientStyles(); }, []);

  // ── Fetch primary data on mount ─────────────────────────────
  const fetchPrimary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meRes, visitsRes, apptRes] = await Promise.all([
        axios.get("/api/patient/me",           { headers: authHeader }),
        axios.get("/api/patient/visits",       { headers: authHeader }),
        axios.get("/api/patient/appointments", { headers: authHeader }),
      ]);
      if (meRes.data.success) {
        setPrimaryPatient(meRes.data.data);
        setActivePatient(meRes.data.data);
        setActivePrn(meRes.data.data.prn);
      }
      if (visitsRes.data.success) setVisits(visitsRes.data.data);
      if (apptRes.data.success)   setAppointments(apptRes.data.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("patientToken");
        navigate("/portal-login", { replace: true });
      } else {
        setError("Failed to load your health data. Please refresh.");
      }
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchPrimary(); }, [fetchPrimary]);

  // ── Switch to a dependent profile ───────────────────────────
  //    (declared BEFORE any effect that references it)
  const handleSwitchDependent = useCallback(async (dep) => {
    if (dep.prn === activePrn) return;

    // Switch to primary (no extra API calls needed)
    if (dep.is_primary) {
      setActivePrn(primaryPatient.prn);
      setActivePatient(primaryPatient);
      setVisits([]);
      setAppointments([]);
      setSwitchLoading(true);
      try {
        const [vRes, aRes] = await Promise.all([
          axios.get("/api/patient/visits",       { headers: authHeader }),
          axios.get("/api/patient/appointments", { headers: authHeader }),
        ]);
        if (vRes.data.success) setVisits(vRes.data.data);
        if (aRes.data.success) setAppointments(aRes.data.data);
      } catch { /* silent */ }
      finally { setSwitchLoading(false); }
      return;
    }

    // Switch to dependent
    setSwitchLoading(true);
    setVisits([]);
    setAppointments([]);
    try {
      const [meRes, vRes, aRes] = await Promise.all([
        axios.get(`/api/patient/dependent/${dep.prn}/me`,           { headers: authHeader }),
        axios.get(`/api/patient/dependent/${dep.prn}/visits`,       { headers: authHeader }),
        axios.get(`/api/patient/dependent/${dep.prn}/appointments`, { headers: authHeader }),
      ]);
      if (meRes.data.success) setActivePatient(meRes.data.data);
      if (vRes.data.success)  setVisits(vRes.data.data);
      if (aRes.data.success)  setAppointments(aRes.data.data);
      setActivePrn(dep.prn);
    } catch {
      setError(`Could not load ${dep.full_name}'s records. Please try again.`);
    } finally {
      setSwitchLoading(false);
    }
  }, [activePrn, primaryPatient, authHeader]);

  // ── Auto-switch to the pinned default profile (runs once) ───
  //    MUST come after handleSwitchDependent is declared.
  useEffect(() => {
    if (didAutoSwitch.current) return;      // only ever once
    if (loading || depsLoading) return;     // wait for both loads
    if (!primaryPatient) return;

    didAutoSwitch.current = true;

    const def = dependents.find(d => d.is_default && !d.is_primary);
    if (def && def.prn !== activePrn) {
      handleSwitchDependent(def);           // reuses existing flow
    }
  }, [loading, depsLoading, dependents, primaryPatient, activePrn, handleSwitchDependent]);

  // ── Switch banner ───────────────────────────────────────────
  const isViewingDependent = activePrn && primaryPatient && activePrn !== primaryPatient.prn;
  const activeDepName      = isViewingDependent
    ? (activePatient?.full_name || activePrn)
    : null;

  const handleNav = (key) => { setActiveNav(key); setMobileMenuOpen(false); };

  const handleLogout = () => {
    localStorage.removeItem("patientToken");
    navigate("/portal-login", { replace: true });
  };

  const handleRefresh = () => {
    if (isViewingDependent) {
      handleSwitchDependent({ prn: activePrn, is_primary: false });
    } else {
      fetchPrimary();
    }
  };

  // ── Render page ─────────────────────────────────────────────
  const renderPage = () => {
    if (loading) {
      return (
        <Flex justify="center" align="center" style={{ minHeight: 400 }}>
          <Spin size="large" />
        </Flex>
      );
    }
    switch (activeNav) {
      case "overview":
        return (
          <PatientOverview
            patient={activePatient}
            visits={visits}
            decoded={decoded}
            appointments={appointments}
            onViewAll={() => handleNav("visits")}
            onViewAllAppointments={() => handleNav("appointments")}
            onDetails={(item) => item?._nav && handleNav(item._nav)}
          />
        );
      case "visits":
        return (
          <PatientVisitHistory
            visits={visits}
            patient={activePatient}
            decoded={decoded}
            token={token}
            dependentPrn={isViewingDependent ? activePrn : null}
            selfPrn={primaryPatient?.prn || decoded?.prn}
          />
        );
      case "appointments":
        return (
          <PatientAppointments
            patient={activePatient}
            decoded={decoded}
            token={token}
            activePrn={activePrn}
            isDependent={isViewingDependent}
          />
        );
      case "investigation":
        return (
          <PatientInvestigation
            visits={visits}
            patient={activePatient}
            decoded={decoded}
            token={token}
            dependentPrn={isViewingDependent ? activePrn : null}
            selfPrn={primaryPatient?.prn || decoded?.prn}
          />
        );
      case "profile":
        return (
          <PatientProfile
            patient={activePatient}
            decoded={decoded}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="pd-root" style={{ background: SURF }}>

      {/* Desktop sidebar */}
      <DesktopSidebar
        patient={primaryPatient}
        decoded={decoded}
        activeNav={activeNav}
        onNav={handleNav}
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        dependentSlot={
          <DependentAvatarRail
            dependents={dependents}
            activePrn={activePrn}
            onSelect={handleSwitchDependent}
            collapsed={sidebarCollapsed}
          />
        }
      />

      {/* Mobile drawer */}
      <MobileDrawer
        patient={primaryPatient}
        decoded={decoded}
        activeNav={activeNav}
        onNav={handleNav}
        onLogout={handleLogout}
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      <div className="pd-main">
        <PatientTopBar
          activeNav={activeNav}
          onRefresh={handleRefresh}
          onLogout={handleLogout}
          onMobileMenu={() => setMobileMenuOpen(true)}
          dependentSlot={
            <DependentSwitcherBtn
              dependents={dependents}
              activePrn={activePrn}
              onClick={() => setDepDrawerOpen(true)}
            />
          }
        />

        <div className="pd-scroll">
          <div className="pd-page-content">

            {/* ── Dependent banner ── */}
            {isViewingDependent && !switchLoading && (
              <div style={{
                background:   "#fffbeb",
                border:       "1.5px solid #fcd34d",
                borderRadius: RADIUS_SM,
                padding:      "10px 16px",
                marginBottom: 18,
                display:      "flex",
                alignItems:   "center",
                justifyContent: "space-between",
                gap:          12,
                flexWrap:     "wrap",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <UserSwitchOutlined style={{ color: "#d97706", fontSize: 16 }} />
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e", fontFamily: FF_BODY }}>
                      Viewing records for:&nbsp;
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f1c2e", fontFamily: FF_BODY }}>
                      {activeDepName}
                    </span>
                    <span style={{
                      marginLeft: 8, fontSize: 10, fontFamily: "monospace",
                      background: "#fef3c7", color: "#92400e",
                      padding: "1px 7px", borderRadius: 20,
                    }}>
                      {activePrn}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => primaryPatient && handleSwitchDependent({ ...primaryPatient, is_primary: true })}
                  style={{
                    background:   "none",
                    border:       "1.5px solid #fcd34d",
                    borderRadius: RADIUS_SM,
                    padding:      "4px 12px",
                    color:        "#92400e",
                    fontSize:     11,
                    fontWeight:   700,
                    cursor:       "pointer",
                    fontFamily:   FF_BODY,
                    minHeight:    32,
                  }}
                >
                  ← Back to my records
                </button>
              </div>
            )}

            {/* ── Switch loading overlay ── */}
            {switchLoading && (
              <div style={{
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                gap:            14,
                padding:        "48px 0",
              }}>
                <Spin size="large" />
                <span style={{ color: "#5e718d", fontSize: 14, fontFamily: FF_BODY }}>
                  Loading records…
                </span>
              </div>
            )}

            {/* ── Error ── */}
            {error && !switchLoading && (
              <Alert
                type="error"
                message={error}
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 18, borderRadius: RADIUS_SM }}
              />
            )}

            {!switchLoading && renderPage()}
          </div>
        </div>
      </div>

      {/* Bottom tab bar */}
      <BottomTabBar activeNav={activeNav} onNav={handleNav} />

      {/* Dependent drawer — ★ default pinning lives inside */}
      <DependentDrawer
        visible={depDrawerOpen}
        onClose={() => setDepDrawerOpen(false)}
        dependents={dependents}
        activePrn={activePrn}
        onSelect={handleSwitchDependent}
        loading={depsLoading}
        token={token}
        onDefaultChanged={refetchDeps}
      />

    </div>
  );
}

export default PatientDashboard;